import { api } from './api.js';

/**
 * fbs#156 (Chase 10-07): no "Sync from Form Builder" button. The practice's Form Builder
 * forms are pulled in on their own whenever an admin or manager opens Forms Hub → Send or a
 * bundle editor (POST /forms/import-builder reads the Form Builder as the signed-in user,
 * which is why the API keeps it to admins and managers).
 *
 * At most one import runs at a time, and not more than once a minute per browser tab, so
 * opening and closing the bundle editor doesn't hammer the Form Builder. A failure is quiet:
 * the forms already synced stay listed.
 */
export const BUILDER_SYNC_MIN_MS = 60_000;

export interface BuilderSyncResult {
  imported: number;
  available: number;
}

type Post = (path: string, body: unknown) => Promise<unknown>;

let lastRun = 0;
let inflight: Promise<BuilderSyncResult | null> | null = null;

export function syncBuilderForms(
  opts: { force?: boolean; now?: () => number; post?: Post } = {},
): Promise<BuilderSyncResult | null> {
  const now = opts.now ?? Date.now;
  if (inflight) return inflight;
  if (!opts.force && lastRun && now() - lastRun < BUILDER_SYNC_MIN_MS) return Promise.resolve(null);
  const post: Post = opts.post ?? ((p, b) => api.post(p, b));
  lastRun = now();
  inflight = post('/forms/import-builder', {})
    .then((r) => {
      const x = (r ?? {}) as Partial<BuilderSyncResult>;
      return { imported: Number(x.imported) || 0, available: Number(x.available) || 0 };
    })
    .catch(() => null)
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

/** Tests only: forget the last run. */
export function resetBuilderSync(): void {
  lastRun = 0;
  inflight = null;
}
